
import { useRef, useCallback } from 'react';
import { startPerformanceTracking, finishPerformanceTracking } from '@/services/ai-analytics-service';
import { TopicType } from '@/types/assistant';
import { logger } from '@/utils/production-logger';

interface PerformanceMetrics {
  requestId: string;
  requestStartTime: number;
  messageProcessingStart: number;
}

interface UsePerformanceTrackingReturn {
  startTracking: () => PerformanceMetrics;
  finishTracking: (
    metrics: PerformanceMetrics,
    userId: string,
    topic?: TopicType,
    status?: 'success' | 'error',
    errorMessage?: string,
    cached?: boolean
  ) => Promise<void>;
  cleanup: () => void;
}

export const usePerformanceTracking = (): UsePerformanceTrackingReturn => {
  const performanceTrackingRef = useRef<Map<string, any>>(new Map());

  const startTracking = useCallback((): PerformanceMetrics => {
    const { requestId, requestStartTime } = startPerformanceTracking();
    const messageProcessingStart = performance.now();
    
    const metrics = {
      requestId,
      requestStartTime: typeof requestStartTime === 'number' ? requestStartTime : Date.now(),
      messageProcessingStart
    };
    
    performanceTrackingRef.current.set(requestId, metrics);
    
    logger.debug('Performance tracking started', {
      requestId,
      component: 'usePerformanceTracking'
    });
    
    return metrics;
  }, []);

  const finishTracking = useCallback(async (
    metrics: PerformanceMetrics,
    userId: string,
    topic?: TopicType,
    status: 'success' | 'error' = 'success',
    errorMessage?: string,
    cached: boolean = false
  ) => {
    try {
      const processingEndTime = performance.now();
      const processingDuration = Math.round(processingEndTime - metrics.messageProcessingStart);
      const totalDuration = Math.round(processingEndTime - metrics.requestStartTime);
      
      await finishPerformanceTracking({
        requestId: metrics.requestId,
        userId,
        operationType: 'chat',
        requestStartTime: metrics.requestStartTime,
        processingDuration,
        totalDuration,
        isCached: cached,
        topic,
        status,
        errorMessage
      });
      
      // Remove from tracking map
      performanceTrackingRef.current.delete(metrics.requestId);
      
      logger.debug('Performance tracking completed', {
        requestId: metrics.requestId,
        processingDuration,
        totalDuration,
        success: status === 'success',
        component: 'usePerformanceTracking'
      });
    } catch (error) {
      logger.error('Error finishing performance tracking', {
        error: error instanceof Error ? error.message : String(error),
        requestId: metrics.requestId,
        component: 'usePerformanceTracking'
      });
    }
  }, []);

  const cleanup = useCallback(() => {
    performanceTrackingRef.current.clear();
    logger.debug('Performance tracking cleanup completed', {
      component: 'usePerformanceTracking'
    });
  }, []);

  return {
    startTracking,
    finishTracking,
    cleanup
  };
};
